import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "../services/adminApi";
import usePolling from "../hooks/usePolling";
import Pagination from "../components/Pagination";

export default function Finance() {
  const [filters, setFilters] = useState({ source: "", status: "", userId: "" });
  const [feed, setFeed] = useState({ items: [], total: 0, page: 1, pageSize: 10 });
  const [withdrawalStats, setWithdrawalStats] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [feedRes, statsRes] = await Promise.all([
        adminApi.getFinanceFeed({
          page: feed.page,
          pageSize: feed.pageSize,
          ...filters,
        }),
        adminApi.getWithdrawalStats(),
      ]);
      setFeed(feedRes);
      setWithdrawalStats(statsRes);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load financial data");
    }
  }, [filters, feed.page, feed.pageSize]);

  usePolling(load, 30000, [
    filters.source,
    filters.status,
    filters.userId,
    feed.page,
    feed.pageSize,
  ]);

  useEffect(() => {
    setFeed((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [filters.source, filters.status, filters.userId]);

  return (
    <div>
      <h2>Financial Monitoring</h2>
      <p className="muted">Auto refresh every 30 seconds.</p>
      <div className="row" style={{ marginBottom: 12 }}>
        <select
          value={filters.source}
          onChange={(e) => setFilters((prev) => ({ ...prev, source: e.target.value }))}
        >
          <option value="">All Sources</option>
          <option value="wallet_transaction">Wallet Transaction</option>
          <option value="blockchain_transaction">Blockchain Transaction</option>
          <option value="crypto_withdrawal">Crypto Withdrawal</option>
          <option value="gold_order">Gold Order</option>
          <option value="gold_audit">Gold Audit</option>
        </select>
        <input
          placeholder="Filter by user ID"
          value={filters.userId}
          onChange={(e) => setFilters((prev) => ({ ...prev, userId: e.target.value }))}
        />
        <input
          placeholder="Filter by status"
          value={filters.status}
          onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
        />
        <button className="btn-primary" onClick={load}>
          Refresh
        </button>
      </div>
      {error ? <div className="error">{error}</div> : null}

      {withdrawalStats ? (
        <div className="row" style={{ marginBottom: 12 }}>
          <div className="card">Pending: {withdrawalStats.pending?.count || 0}</div>
          <div className="card">Processing: {withdrawalStats.processing || 0}</div>
          <div className="card">Confirmed: {withdrawalStats.confirmed?.count || 0}</div>
          <div className="card">Failed: {withdrawalStats.failed || 0}</div>
          <div className="card">Rejected: {withdrawalStats.rejected || 0}</div>
        </div>
      ) : null}

      <div className="card">
        <h3>Unified Financial Feed</h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Source</th>
                <th>User</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {feed.items.map((item) => (
                <tr key={`${item.source}-${item.id}`}>
                  <td>{new Date(item.createdAt).toLocaleString()}</td>
                  <td>{item.source}</td>
                  <td>{item.user?.email || item.userId || "-"}</td>
                  <td>{item.eventType}</td>
                  <td>{item.amount ?? item.totalAmount ?? "-"}</td>
                  <td>{item.status || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={feed.page}
          pageSize={feed.pageSize}
          total={feed.total}
          onPageChange={(nextPage) =>
            setFeed((prev) => ({
              ...prev,
              page: nextPage,
            }))
          }
        />
      </div>
    </div>
  );
}
